// Accounts, history, and the meter: the HTTP shapes the web client (and later
// any other client) shares with the Worker, plus the pure rules both sides
// apply (what a handle may be, how usage becomes a cap state). DOM-free, like
// everything in this package, so a React Native client can import it.

import type { AccentHue, Appearance, MeterSettings } from "./settings.js";
import type { WrapStage } from "./wrap.js";

// ---- Handles ------------------------------------------------------------

/** A handle is the personal room's address, `/<handle>`: lowercase letters,
 * digits and inner hyphens, 2 to 30 long. */
export const HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,28}[a-z0-9])$/;

/** Paths the app itself uses, which a handle must never shadow. */
export const RESERVED_HANDLES: ReadonlySet<string> = new Set([
  "j", "api", "app", "admin", "home", "signin", "signup", "signout", "login", "logout",
  "settings", "account", "calls", "call", "recordings", "recording", "privacy", "terms",
  "help", "about", "assets", "fonts", "static", "favicon", "icon", "icons", "manifest",
  "robots", "sitemap", "new", "join", "breeze", "seamless", "www", "support", "listen",
  "prep", "practice", "record",
]);

/** The handle whose room is the bare domain. Adam, 2026-09-28: "can we
 * just have breeze.adammirmina.com do that job? anyoneelse who uses the app
 * will be on the / after it for their own breeze rooms". `/<handle>` opens
 * the same room, so links already sent keep working. */
export const ROOT_HANDLE = "adam";

/** The path of a handle's room: "/" for the root handle, `/<handle>` for
 * everyone else. */
export function roomPath(handle: string): string {
  return handle.toLowerCase() === ROOT_HANDLE ? "/" : `/${handle}`;
}

/** A room's full link, written without a trailing slash for the root. */
/** Where a room's page lives. Adam, 2026-09-28: "i guess call my room
 * meet.adammirmina.com then, yeah?"; Breeze is the name of the AI caller, and
 * rooms carry no brand. breeze.adammirmina.com still serves the API, and a
 * page visit there redirects here, so links already sent keep working. A
 * local or preview origin is left as it is. */
export const MEET_ORIGIN = "https://meet.adammirmina.com";

export function roomOrigin(origin: string): string {
  return /(^|\.)adammirmina\.com$/.test(/^[a-z]+:\/\/([^/:?#]+)/i.exec(origin)?.[1] ?? "") ? MEET_ORIGIN : origin;
}

export function roomLink(origin: string, handle: string): string {
  const path = roomPath(handle);
  return path === "/" ? origin : `${origin}${path}`;
}

/** What a personal room is called on its join page and in a shared link's
 * preview. Adam, 2026-09-28: "i want this to say Adam Mirmina's Breeze
 * Room" (his naming of Breeze here overrides the no-branding rule). */
export function roomTitle(ownerName: string): string {
  return `${ownerName}'s room`;
}

export type HandleProblem = "format" | "reserved";

export function handleProblem(handle: string): HandleProblem | null {
  if (!HANDLE_PATTERN.test(handle)) return "format";
  if (RESERVED_HANDLES.has(handle)) return "reserved";
  return null;
}

/** Turns a display name into a handle suggestion: "Adam Mirmina" to
 * "adam-mirmina". The person can change it before signing up. */
export function suggestHandle(name: string): string {
  const base = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30)
    .replace(/-+$/g, "");
  return base.length >= 2 ? base : "";
}

// ---- One-off links ------------------------------------------------------

/** One-off room codes: 10 characters from an alphabet without look-alikes,
 * about 50 bits, so a code cannot be guessed. Typeable, and short enough to
 * read aloud. */
export const ONCE_CODE_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";
export const ONCE_CODE_LENGTH = 10;
export const ONCE_CODE_PATTERN = /^[a-z0-9]{6,16}$/;

/** A fresh code from 10 random bytes. `random` fills a byte array (crypto's
 * getRandomValues in both the browser and the Worker). */
export function newOnceCode(random: (bytes: Uint8Array) => Uint8Array): string {
  const bytes = random(new Uint8Array(ONCE_CODE_LENGTH));
  let out = "";
  for (const b of bytes) out += ONCE_CODE_ALPHABET[b % ONCE_CODE_ALPHABET.length];
  return out;
}

// ---- The account ---------------------------------------------------------

/** What an account keeps in `users.settings`. Every field optional: an
 * absent field means the default. */
export interface UserSettings {
  readonly accent?: AccentHue;
  /** Light, dark, or auto (the device's own setting). Light when absent. */
  readonly appearance?: Appearance;
  /** Start recording on its own when this account hosts a call (BRIEF 9),
   * in its personal room and in one-off links it made. Off when absent, so
   * a new account records nothing it did not ask for; Adam's own account
   * has it on (2026-09-28, "fix those gaps"). The host can still stop it. */
  readonly autoRecord?: boolean;
  /** Knock in the personal room. On by default. */
  readonly knock?: boolean;
  /** BRIEF 12.1: where a finished recording is POSTed, and the bearer key
   * sent with it. */
  readonly recording_webhook_url?: string;
  readonly recording_webhook_key?: string;
}

export interface AccountUser {
  readonly id: string;
  readonly handle: string;
  readonly displayName: string;
  readonly email: string;
  readonly settings: UserSettings;
}

/** `POST /api/auth/signup` and `POST /api/auth/signin` answer with the
 * account and a user key (worker/src/userKeys.ts) the client stores. */
export interface AuthResponse {
  readonly key: string;
  readonly user: AccountUser;
}

export interface SignUpRequest {
  readonly name: string;
  readonly handle: string;
  readonly email: string;
  readonly password: string;
}

export interface SignInRequest {
  readonly email: string;
  readonly password: string;
}

/** `PATCH /api/me`. `settings` merges into what is stored. */
export interface UpdateAccountRequest {
  readonly displayName?: string;
  readonly settings?: UserSettings;
  readonly password?: string;
}

/** Field-level refusals the sign-up and settings forms show beside a field. */
export type AccountErrorField = "name" | "handle" | "email" | "password";

export interface AccountError {
  readonly error: true;
  readonly code: string;
  readonly message: string;
  readonly field?: AccountErrorField;
}

// ---- History --------------------------------------------------------------

export interface CallParticipantRecord {
  /** The Breeze user id when they joined signed in, else empty. */
  readonly user: string;
  readonly name: string;
  readonly joined: string;
  readonly left: string;
}

export interface CallSummary {
  readonly id: string;
  readonly roomKind: "personal" | "once";
  readonly roomSlug: string;
  readonly started: string;
  /** Empty while the call is still going. */
  readonly ended: string;
  readonly participants: readonly CallParticipantRecord[];
  readonly recording: RecordingSummary | null;
}

/** `GET /api/people`: the Breeze accounts the signed-in user has been in a
 * call with (their room, or a call they were rung into or rang), newest
 * first. It is what a "who do you want to call" picker lists; ringing one is
 * `POST /api/lines/<handle>/ring`. */
export interface PersonSummary {
  readonly id: string;
  readonly handle: string;
  readonly displayName: string;
  /** When they were last in a call together (ISO). */
  readonly lastCall: string;
  readonly calls: number;
  /** A phone is registered for their line, so ringing reaches a device. A
   * person without one can still be rung; it reaches an open Breeze tab only. */
  readonly ringable: boolean;
}

export interface RecordingSummary {
  readonly id: string;
  readonly status: "recording" | "processing" | "ready" | "failed";
  readonly durationSecs: number;
}

export interface TranscriptLineRecord {
  readonly speaker: string;
  readonly start: number;
  readonly end: number;
  readonly text: string;
}

export interface RecordingDetail extends RecordingSummary {
  readonly callId: string;
  readonly started: string;
  readonly transcript: readonly TranscriptLineRecord[];
  /** Markdown notes on the call, written from the transcript; empty until
   * the transcript is done, and when there was nothing to summarize. */
  readonly summary: string;
  /** Commitments made on the call, each with who took it on (may be empty). */
  readonly actionItems: readonly { readonly description: string; readonly assignee: string }[];
  readonly files: readonly { readonly label: string; readonly url: string }[];
}

// ---- The meter (BRIEF 13) --------------------------------------------------

export type CapState = "ok" | "warn_80" | "block_95";

export interface UsageState {
  /** Month to date, in GB. */
  readonly egressGb: number;
  readonly freeGb: number;
  readonly percent: number;
  readonly capState: CapState;
  /** "estimate" counts from Breeze's own call records; "cloudflare" is the
   * account's measured egress. */
  readonly source: "estimate" | "cloudflare";
  readonly asOf: string;
}

/** Pass `DEFAULT_SETTINGS.meter`, or a plan's own thresholds. */
export function capStateFor(egressGb: number, meter: MeterSettings): CapState {
  const percent = (egressGb / meter.freeGbPerMonth) * 100;
  if (percent >= meter.blockAtPercent) return "block_95";
  if (percent >= meter.warnAtPercent) return "warn_80";
  return "ok";
}

/** GB of SFU egress one participant causes per hour for each other person
 * in the call: BRIEF 11 puts a 1:1 720p call at 1.2 to 1.4 GB per call-hour
 * with both directions counted, so about 0.65 per person per other person. */
export const EGRESS_GB_PER_PERSON_HOUR = 0.65;

/** Month-to-date egress estimated from call records: every participant pulls
 * every other participant's streams for as long as they overlap. A call
 * still going counts up to `now`. Records outside the month count only
 * their part inside it. */
export function estimateEgressGb(
  calls: readonly { readonly participants: readonly CallParticipantRecord[] }[],
  monthStart: Date,
  now: Date,
): number {
  const from = monthStart.getTime();
  const to = now.getTime();
  let gb = 0;
  for (const call of calls) {
    const spans = call.participants
      .map((p) => {
        const a = Math.max(Date.parse(p.joined), from);
        const b = Math.min(p.left ? Date.parse(p.left) : to, to);
        return Number.isFinite(a) && Number.isFinite(b) && b > a ? [a, b] as const : null;
      })
      .filter((s): s is readonly [number, number] => s !== null);
    for (let i = 0; i < spans.length; i++) {
      for (let j = 0; j < spans.length; j++) {
        if (i === j) continue;
        const overlap = Math.min(spans[i]![1], spans[j]![1]) - Math.max(spans[i]![0], spans[j]![0]);
        if (overlap > 0) gb += (overlap / 3_600_000) * EGRESS_GB_PER_PERSON_HOUR;
      }
    }
  }
  return Math.round(gb * 1000) / 1000;
}

/** The first instant of `now`'s month, in UTC (Cloudflare bills by UTC
 * calendar month). */
export function utcMonthStart(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

/** One booked call ahead of the account's owner, for the desktop home. */
export interface HomeCall {
  readonly id: string;
  /** Who is coming, as booked. Empty when the booking has no name. */
  readonly guest: string;
  readonly startsAt: string;
  readonly endsAt: string;
  /** How the call happens: this room, a phone call, or a link elsewhere. */
  readonly via: "breeze" | "phone" | "link";
  /** A link to join, for a `link` call. Empty otherwise. */
  readonly join: string;
  /** The prep page for this booking, when one exists. */
  readonly prepUrl: string;
  /** The prep page's slug, for opening it inside Breeze. Empty when none. */
  readonly prepSlug: string;
}

/** One line of a prep page: a bold opening question or point, the rest of it,
 * and any indented follow-ups. */
export interface PrepItem {
  readonly lead: string;
  readonly text: string;
  readonly subs: readonly { readonly lead: string; readonly text: string }[];
}

export interface PrepSection {
  readonly heading: string;
  /** Who the person is: shown last and closed. */
  readonly background: boolean;
  /** Starts closed in every view; its heading is the toggle. */
  readonly fold?: boolean;
  readonly items: readonly PrepItem[];
}

/** `GET /api/prep?slug=` or `?now=1` (operator only): a meeting's prep page. */
export interface PrepData {
  readonly slug: string;
  readonly title: string;
  readonly meta: readonly string[];
  readonly startsAt: string;
  readonly minutes: number;
  readonly join: string;
  /** The person's LinkedIn profile, or empty. */
  readonly linkedin: string;
  readonly sections: readonly PrepSection[];
}

/** One section of an editable prep: its id, its heading, and its html (ul/li,
 * b, mark, p, br). */
export interface PrepDocSection {
  readonly id: string;
  readonly title: string;
  readonly html: string;
  /** Starts closed in every view; its heading is the toggle. Not saved. */
  readonly fold?: boolean;
}

/** `GET /api/prep/doc?slug=` (operator only): a prep as the editor shows it,
 * with every saved edit applied. Share's `script_docs` is the store, so an
 * edit made on the share page and one made here are the same edit. */
export interface PrepDoc {
  readonly slug: string;
  readonly title: string;
  readonly meta: readonly string[];
  readonly startsAt: string;
  readonly minutes: number;
  readonly join: string;
  readonly linkedin: string;
  /** Made in Breeze (no markdown file under it). */
  readonly created: boolean;
  /** The page on adammirmina.com. */
  readonly url: string;
  readonly sections: readonly PrepDocSection[];
}

/** The order, headings, and removals on top of a prep's own sections. */
export interface PrepLayout {
  readonly order?: readonly string[];
  readonly titles?: Readonly<Record<string, string>>;
  readonly deleted?: readonly string[];
}

/** `GET /api/prep/list`: every prep, upcoming soonest first, then past. */
export interface PrepListRow {
  readonly slug: string;
  readonly title: string;
  readonly startsAt: string;
  readonly minutes: number;
  readonly when: string;
  readonly created: boolean;
  readonly past: boolean;
}

/** One turn of the prep chat. */
export interface PrepChatTurn {
  readonly role: "user" | "breeze";
  readonly text: string;
}

/** `POST /api/prep/chat`: Breeze's answer and what it changed. */
export interface PrepChatReply {
  readonly reply: string;
  /** Preps it edited, so an open editor reloads them. */
  readonly changed: readonly string[];
  /** A prep it made, if any. */
  readonly created: { readonly slug: string; readonly title: string } | null;
  /** Standing rules this message saved or removed, and requests it passed to
   * the Seamless chat. Absent when none. */
  readonly rules?: { readonly saved: number; readonly removed: number; readonly handedOff: number };
}

/** `GET /api/home`: what Breeze has for the signed-in owner before they go
 * in. `upcoming` is null for an account that has no calendar behind it. */
export interface HomeData {
  readonly operator: boolean;
  readonly inRoom: number;
  readonly waiting: readonly { readonly name: string; readonly since: string }[];
  readonly upcoming: readonly HomeCall[] | null;
  /** The calendar could not be read just now. */
  readonly upcomingFailed: boolean;
  /** The owner's last call, once it is over: Rejoin and Finish until they
   * press Finish, then what came out of it and where it went. Null while a
   * call is on, and once the finished one has had its day. */
  readonly wrap: HomeWrap | null;
  /** The owner's latest call that nobody has finished, on now or over:
   * Seamless's Calls row shows Rejoin, and Finish once `engaged`. Null when
   * every call is finished or there is none worth finishing. */
  readonly open: HomeOpen | null;
}

/** A call still waiting for Finish. */
export interface HomeOpen {
  readonly id: string;
  readonly started: string;
  /** Empty while the call is on. */
  readonly ended: string;
  /** The owner and a guest were on the call together for two minutes or more. */
  readonly engaged: boolean;
  /** When Finish becomes due if everyone now on the call stays; empty when
   * it is already due or nobody is together right now. */
  readonly engagedAt: string;
  /** The host pressed Persist: the card stays until they Finish it. */
  readonly persist?: boolean;
}

/** The home card for a call that is over (core/src/wrap.ts). */
export interface HomeWrap {
  readonly id: string;
  /** The host pressed Persist: the card stays until they Finish it. */
  readonly persist?: boolean;
  readonly started: string;
  readonly ended: string;
  /** Empty until the host presses Finish (or the room finishes it after
   * thirty minutes with nobody back). */
  readonly finished: string;
  /** Everyone else on the call, by the names they joined under. */
  readonly guests: readonly string[];
  /** The room to go back into (`/adam`, `/j/<code>`). */
  readonly rejoin: string;
  /** The assistant's summary of the call, or the recording's notes. */
  readonly summary: string;
  /** The shared notes, in order. */
  readonly points: readonly string[];
  readonly steps: readonly { readonly text: string; readonly owner: string; readonly done: boolean }[];
  readonly stages: readonly WrapStage[];
  /** One plain line on whether the recording saved ("Recording saved,
   * 41 min", "Saving the recording", "The recording did not save"), shown
   * on the card before and after Finish (core savedLine). */
  readonly saved: string;
}

// ---- Call history (QUEUE rows 282 and 283) -----------------------------------
// Every call the account recorded, with its analysis. For the operator the
// store is the call archive on ramsgatestudio.com, which every finished
// Breeze recording is sent into and which also holds the Fathom ones; any
// other account gets its own Breeze recordings in the same shape. Home on
// meet.* and Seamless's Calls screen both read this, so neither holds a copy.

export type HistoryKind = "business" | "mentorship" | "family" | "other";
export const HISTORY_KINDS: readonly HistoryKind[] = ["business", "mentorship", "family", "other"];

export interface HistoryCall {
  readonly id: string;
  readonly source: "fathom" | "breeze";
  readonly title: string;
  /** ISO, UTC. */
  readonly started: string;
  readonly minutes: number;
  /** Everyone on the call but the account. */
  readonly people: readonly string[];
  /** One line on what the call was for; may be empty. */
  readonly purpose: string;
  readonly kind: HistoryKind;
  /** The word wall exists. */
  readonly hasAnalysis: boolean;
  readonly hasTranscript: boolean;
}

export interface CallWallItem {
  readonly text: string;
  readonly who: string;
  readonly kind: "quote" | "idea";
  /** 1 to 5. */
  readonly weight: number;
  /** Said by the account reading it, so a quote of theirs sits in ink and
   * everyone else's in the muted accent (wallStyle). */
  readonly mine: boolean;
}

/** The analysis of a call: private to its owner, never on a guest's screen. */
export interface CallWall {
  readonly gist: string;
  readonly items: readonly CallWallItem[];
  readonly decided: readonly string[];
  readonly next: readonly string[];
  readonly take: string;
}

export interface HistoryLine {
  readonly speaker: string;
  /** "hh:mm:ss" into the call. */
  readonly at: string;
  readonly text: string;
}

export interface HistoryDetail extends HistoryCall {
  readonly ended: string;
  /** Markdown, in the shape of Fathom's summary. */
  readonly summary: string;
  readonly actionItems: readonly { readonly description: string; readonly assignee: string; readonly done: boolean }[];
  readonly transcript: readonly HistoryLine[];
  readonly wall: CallWall | null;
  readonly watchUrl: string | null;
  /** Business calls, written after the call from the clean transcript: one
   * sentence each. Absent until written. */
  readonly takeaways?: readonly string[];
  /** What the owner learned about the other side's industry and problems. */
  readonly learned?: readonly string[];
}
